// src/analytics/analytics.js
// ✅ GA4 Event Tracking Utility for Piano With Pan
// All custom events for conversion tracking and user engagement

export const GA_MEASUREMENT_ID = 'G-TZC5XT1878'; // ⚠️ Replace with your actual GA4 Measurement ID

/**
 * Helper to safely send events to GA4
 * Only fires if gtag is loaded (respects cookie consent)
 */
export const sendGAEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
    console.log('📊 GA Event:', eventName, eventParams); // For debugging
  } else {
    console.log('⚠️ GA not loaded yet (user may not have accepted cookies)');
  }
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CONVERSION EVENTS (Critical for revenue tracking)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Track when user views the paywall
 * Call this in PwpPaywall component on mount
 */
export const trackViewPaywall = () => {
  sendGAEvent('view_paywall', {
    page_location: window.location.href,
    page_title: document.title,
    page_path: window.location.pathname,
  });
};

/**
 * Track when user clicks to start payment (begin checkout)
 * Call this when user clicks any plan's payment button
 * 
 * @param {string} planId - The plan identifier (e.g., 'daily', 'weekly', 'monthly')
 * @param {number} amount - The price in rupees
 */
export const trackBeginCheckout = (planId, amount) => {
  sendGAEvent('begin_checkout', {
    currency: 'INR',
    value: amount,
    items: [{
      item_id: planId,
      item_name: `Piano With Pan ${planId} Pass`,
      price: amount,
      quantity: 1,
    }]
  });
};

/**
 * Track successful purchase (CRITICAL for revenue tracking)
 * Call this on payment success page after confirming payment
 * 
 * @param {string} transactionId - Unique payment/order ID from Razorpay
 * @param {number} amount - The price paid in rupees
 * @param {string} planId - The plan purchased
 */
export const trackPurchase = (transactionId, amount, planId) => {
  sendGAEvent('purchase', {
    transaction_id: transactionId,
    value: amount,
    currency: 'INR',
    items: [{
      item_id: planId,
      item_name: `Piano With Pan ${planId} Pass`,
      price: amount,
      quantity: 1,
    }]
  });
};

/**
 * Track user sign up / registration
 * Call this when a new user completes registration
 * 
 * @param {string} method - How they signed up (e.g., 'email', 'google', 'oauth')
 */
export const trackSignUp = (method = 'email') => {
  sendGAEvent('sign_up', {
    method: method
  });
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ENGAGEMENT EVENTS (Track user progress and retention)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Track when user completes a level
 * Call this when a level is successfully completed
 * 
 * @param {number} levelNumber - The level number (1-10)
 * @param {number} timeSpent - Time spent on this level in seconds (optional)
 */
export const trackLevelComplete = (levelNumber, timeSpent = null) => {
  const params = {
    level_number: levelNumber,
    level_name: `Level ${levelNumber}`,
  };
  
  if (timeSpent !== null) {
    params.time_spent = timeSpent;
  }
  
  sendGAEvent('level_complete', params);
};

/**
 * Track session time milestones (deep engagement)
 * Call this when user reaches time milestones (5min, 10min, 20min)
 * 
 * @param {number} minutes - Number of minutes in session
 */
export const trackSessionMilestone = (minutes) => {
  sendGAEvent('session_milestone', {
    milestone_minutes: minutes,
    engagement_type: 'time_based'
  });
};

/**
 * Track when user starts practice mode
 */
export const trackPracticeModeStart = () => {
  sendGAEvent('practice_mode_start', {
    page_location: window.location.href,
  });
};

/**
 * Track when user views help/tutorial
 */
export const trackHelpView = () => {
  sendGAEvent('help_view', {
    page_location: window.location.href,
  });
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// UTILITY: Session Time Tracking
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

let sessionTimers = [];

/**
 * Start tracking session time milestones
 * Call this in App.js useEffect on mount
 * Automatically tracks at 5min, 10min, and 20min
 */
export const startSessionTracking = () => {
  // Clear any existing timers first
  stopSessionTracking();
  
  const milestones = [5, 10, 20]; // minutes
  
  milestones.forEach(minutes => {
    const timer = setTimeout(() => {
      trackSessionMilestone(minutes);
    }, minutes * 60 * 1000);
    
    sessionTimers.push(timer);
  });
};

/**
 * Stop session time tracking
 * Call this on unmount or when cleaning up
 */
export const stopSessionTracking = () => {
  sessionTimers.forEach(timer => clearTimeout(timer));
  sessionTimers = [];
};